import { useQuery } from '@apollo/client/react';

import type { StepListingsCompareFragment } from '@graphql/__generated__/graphql';
import RaGrid from '@modules/_shared/components/RaGrid';
import ComparableListingsSkeleton from '@modules/comparable-listings/ComparableListingsSkeleton';
import { STEP_LISTINGS_COMPARE_ALGO } from '@modules/reports/cma-reports/cmaReportsQueries';
import CmaScraperCard from '@modules/reports/cma-reports/step-listing-compare/CmaScraperCard';
import { SelectableCard } from '@modules/reports/cma-reports/step-listing-compare/SelectableCard';
import type { CmaListingWithDistance } from '@modules/reports/cma-reports/step-listing-compare/types';
import { getAutoAlgoParams } from '@modules/reports/cma-reports/utils';
import { useLocale } from '@shared/hooks/locale';
import type { SetHook } from '@shared/hooks/useSet';

interface ListingAutomaticProps {
  data: StepListingsCompareFragment;
  onSelect: (id: string | number) => void;
  selection: SetHook<string | number>;
}

const ListingAutomatic = ({
  selection,
  onSelect,
  data: fragmentData,
}: ListingAutomaticProps) => {
  const { countryCode } = useLocale();

  const autoAlgoParams = getAutoAlgoParams(fragmentData?.property, countryCode);
  const { data, loading } = useQuery(STEP_LISTINGS_COMPARE_ALGO, {
    ...autoAlgoParams,
  });

  const cmaListingsDistance = (
    data?.ch_get_similar_listings_cohorts ??
    data?.es_get_similar_listings_cohorts ??
    data?.fr_get_similar_listings_cohorts ??
    data?.it_get_similar_listings_cohorts ??
    data?.pl_get_similar_listings_cohorts ??
    data?.pt_get_similar_listings_cohorts ??
    []
  ).filter((item): item is CmaListingWithDistance => item.listing?.id != null);

  const renderItemCard = (listing: CmaListingWithDistance) => {
    const isSelected = selection.has(listing.listing.id);
    return (
      <SelectableCard
        selected={isSelected}
        onChange={() => onSelect(listing.listing.id)}
      >
        <CmaScraperCard cmaListingDistance={listing} variant="vertical" />
      </SelectableCard>
    );
  };

  return (
    <RaGrid
      items={cmaListingsDistance}
      loading={loading}
      pageSize={12}
      renderItemCard={renderItemCard}
      renderSkeleton={() => <ComparableListingsSkeleton />}
      selection={selection as SetHook<string | number>}
    />
  );
};

export default ListingAutomatic;
